import { zodResolver } from '@hookform/resolvers/zod';
import {
  LOGIN_FORM_MESSAGES,
  loginFormSchema,
  type LoginFormInput,
  type LoginFormValues,
} from '@groundly/shared';
import { useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { Eye, EyeOff } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { isUnauthenticated } from '../api/call.js';
import { useMe } from '../api/me.js';
import { logIn } from '../auth/auth-client.js';
import { useCarriedValues } from '../auth/navigation-state.js';
import { clearsPassword, type LoginOutcome } from '../auth/login-outcome.js';
import { safeReturnTo } from '../auth/return-to.js';
import { useLandingRedirect } from '../auth/use-landing-redirect.js';
import { Button, InlineAction } from '../components/Button.js';
import { DangerAlert } from '../components/DangerAlert.js';
import { PageHeading } from '../components/PageHeading.js';
import { CheckFailed, CheckingSession } from '../components/SessionStates.js';
import { TextField } from '../components/TextField.js';
import { PreAccountCard, PreAccountFootnote, PreAccountFrame } from '../frame/PreAccountFrame.js';
import alertStyles from '../components/alert.module.css';

/**
 * The log in screen (`ux.md`, Flows A, B and D). It runs the session check
 * first: a logged-in Account User goes straight to their landing destination
 * and never sees the form, and any `returnTo` is ignored for them.
 */
const loginSearchSchema = z.object({ returnTo: z.string().optional() });

export const Route = createFileRoute('/login')({
  validateSearch: (search: Record<string, unknown>) => {
    const parsed = loginSearchSchema.safeParse(search);
    return parsed.success ? parsed.data : {};
  },
  component: LoginScreen,
});

function LoginScreen() {
  const { returnTo } = Route.useSearch();
  const me = useMe();
  const [loggedIn, setLoggedIn] = useState(false);

  // Flow D: already logged in, so the landing applies and the return URL does
  // not. Switched off the moment log in succeeds, because the return URL then
  // decides where to go instead (Flow B8).
  const landing = useLandingRedirect(me.isSuccess && !loggedIn);

  if (me.isError && !isUnauthenticated(me.error)) {
    return (
      <CheckFailed
        onRetry={() => {
          void me.refetch();
        }}
      />
    );
  }

  if (me.isPending || me.isSuccess || loggedIn) {
    if (landing.kind === 'failed') return <CheckFailed onRetry={landing.retry} />;
    return <CheckingSession />;
  }

  return <LoginForm returnTo={returnTo} onLoggedIn={() => setLoggedIn(true)} />;
}

interface LoginFormProps {
  returnTo: string | undefined;
  onLoggedIn: () => void;
}

function LoginForm({ returnTo, onLoggedIn }: LoginFormProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [outcome, setOutcome] = useState<LoginOutcome | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [signedIn, setSignedIn] = useState(false);
  // Opened from Check your email's Log in link, the address comes along
  // (account sign-up `ux.md`, flow E3), never in the URL.
  const carried = useCarriedValues();

  const {
    register,
    handleSubmit,
    setError,
    setFocus,
    setValue,
    getValues,
    trigger,
    formState: { errors, dirtyFields, isSubmitting },
  } = useForm<LoginFormInput, unknown, LoginFormValues>({
    resolver: zodResolver(loginFormSchema),
    defaultValues: { email: carried.email, password: '' },
    // Nothing is checked while a field is typed into for the first time. Once a
    // field shows an error it is rechecked as it is edited
    // (`patterns/ui-patterns.md`, 7).
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  });

  const email = register('email');

  const onSubmit = handleSubmit(async (values) => {
    setOutcome(null);
    const result = await logIn(values);

    if (result.kind === 'success') {
      setSignedIn(true);
      onLoggedIn();
      // No page may show the previous Account User's data
      // (`patterns/frontend-conventions.md`, 1).
      queryClient.clear();
      // The return URL if it passes every rule, otherwise `/`, which lands.
      // Either way the log in screen is replaced, so Back never returns to it.
      await navigate({ href: safeReturnTo(returnTo) ?? '/', replace: true });
      return;
    }

    if (result.kind === 'email-format') {
      // Better Auth's own format check, shown on the field it belongs to.
      setError('email', { message: LOGIN_FORM_MESSAGES.emailFormat });
      setFocus('email');
      return;
    }

    setOutcome(result);
    // After a refusal the password is cleared; after a failure it is kept,
    // because the Account User did nothing wrong (`ux.md`, States).
    if (clearsPassword(result)) setValue('password', '');
    setFocus('password');
  });

  const alert = outcome === null ? null : alertFor(outcome);
  // The not-verified alert leads to a new verification email, for the address
  // that was typed (account sign-up `ux.md`, flow E2).
  const alertAction =
    outcome?.kind === 'unverified' ? (
      <Link
        to="/verify-email/resend"
        state={{ email: getValues('email').trim() }}
        className={alertStyles.link}
      >
        Get a new verification email
      </Link>
    ) : undefined;
  // The button keeps "Logging in" until the destination opens.
  const isBusy = isSubmitting || signedIn;

  return (
    <PreAccountFrame>
      <PreAccountCard
        as="form"
        labelledBy="login-heading"
        onSubmit={(event) => void onSubmit(event)}
        head={<PageHeading id="login-heading">Log in</PageHeading>}
      >
        {alert === null ? null : (
          <DangerAlert title={alert.title} action={alertAction}>
            {alert.body}
          </DangerAlert>
        )}

        <TextField
          {...email}
          id="email"
          label="Email address"
          type="email"
          autoComplete="username"
          autoCapitalize="none"
          spellCheck={false}
          error={errors.email?.message}
          onChange={(event) => {
            void email.onChange(event);
            // A format error shown on leaving the field is rechecked as the
            // field is edited, before any submit too, and clears as soon as it
            // is fixed (`patterns/ui-patterns.md`, 7). After a submit,
            // `reValidateMode` already does this.
            if (errors.email !== undefined) void trigger('email');
          }}
          onBlur={(event) => {
            void email.onBlur(event);
            // The format check also runs when the field is left after a change
            // (`patterns/ui-patterns.md`, 7).
            if (dirtyFields.email === true) void trigger('email');
          }}
        />

        <TextField
          {...register('password')}
          id="password"
          label="Password"
          type={showPassword ? 'text' : 'password'}
          autoComplete="current-password"
          error={errors.password?.message}
          labelAction={
            <InlineAction
              aria-controls="password"
              onClick={() => setShowPassword((shown) => !shown)}
              icon={
                showPassword ? (
                  <EyeOff aria-hidden="true" strokeWidth={1.75} width={14} height={14} />
                ) : (
                  <Eye aria-hidden="true" strokeWidth={1.75} width={14} height={14} />
                )
              }
            >
              {showPassword ? 'Hide password' : 'Show password'}
            </InlineAction>
          }
        />

        <Button type="submit" variant="primary" block disabled={isBusy}>
          {isBusy ? 'Logging in' : 'Log in'}
        </Button>
      </PreAccountCard>

      <PreAccountFootnote>
        Forgot your password?{' '}
        <Link
          to="/forgot-password"
          state={(previous) => ({ ...previous, email: getValues('email').trim() })}
        >
          Reset it
        </Link>
      </PreAccountFootnote>
      <PreAccountFootnote>
        New to Groundly? <Link to="/signup">Sign up</Link>
      </PreAccountFootnote>
      {/*
        For anyone whose verification email was lost, including an Account User
        added by another, who has no password to log in with until they set one
        from it (users-and-permissions `technical.md`, Dashboard, Log in screen).
        The typed address goes along, never in the URL.
      */}
      <PreAccountFootnote>
        Didn&apos;t get your verification email?{' '}
        <Link
          to="/verify-email/resend"
          state={(previous) => ({ ...previous, email: getValues('email').trim() })}
        >
          Get a new one
        </Link>
      </PreAccountFootnote>
    </PreAccountFrame>
  );
}

/** The danger alert each refused or failed attempt shows (`ux.md`, Content). */
function alertFor(outcome: LoginOutcome): { title: string; body: string } {
  switch (outcome.kind) {
    case 'incorrect':
      return {
        title: 'Email address or password is incorrect',
        body: 'Check both and try again.',
      };
    case 'unverified':
      return {
        title: 'Email address not verified',
        body: "Open the link in the verification email sent to this address, then log in again. If you can't find it, check your spam or junk folder.",
      };
    case 'too-many-attempts':
      return {
        title: 'Too many login attempts',
        body: waitBody(outcome.minutes),
      };
    case 'no-response':
      return {
        title: "Couldn't log in",
        body: "Groundly couldn't be reached. Check your internet connection and try again.",
      };
    default:
      return {
        title: "Couldn't log in",
        body: 'Groundly had a problem logging you in. Try again in a moment.',
      };
  }
}

function waitBody(minutes: number | null): string {
  if (minutes === null) return 'Try again later.';
  return minutes === 1 ? 'Try again in 1 minute.' : `Try again in ${minutes} minutes.`;
}
